import { useMemo, useState, useEffect } from 'react';

import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';

import { getReportDefinitionsPaginated } from '../../../../redux/slices/reportsSlice';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { getActions, ActionDropdown, type ActionEntity } from 'actions';

const toActionEntity = (row: any): ActionEntity => ({ type: 'report_definition', id: row.rd_id });

const ListReportDefinitions = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { reportDefinitions, config: headerConfig, filtersConfig, pagination, error, status, loading } = useAppSelector((s) => (s as any).reports);

  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'report_definitions';
  const [showSidebar, setShowSidebar] = useState(false);

  const isLoading = loading?.getReportDefinitionsPaginated ?? false;

  // Initial fetch
  useEffect(() => {
    if (status === null) {
      dispatch(getReportDefinitionsPaginated({}) as any);
    }
  }, [dispatch, status]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: getReportDefinitionsPaginated,
    columnFormats: { created_at: 'datetime' },
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh });
      return <ActionDropdown actions={actions} items={['edit']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });
  const columns = [...baseColumns, folderColumn, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(reportDefinitions || []), [reportDefinitions]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: false,
  });

  const { currentPage, totalPages, totalItems } = pagination;

  return (
    <>
      {error && <Alert variant="danger">{t('error-generic')}</Alert>}

      <TableViewLayout
        filtersConfig={filtersConfig}
        filters={filters}
        pageName={pageName}
        columns={(headerConfig ?? []).map((c: any) => ({
          key: c.header_key,
          label: c.label,
        }))}
        pageSize={limit}
        error={error}
        onFilterChange={setFilterAndSync}
        onResetFilters={resetFilters}
        onPageSizeChange={updateLimit}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={updatePage}
        table={table}
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        onRefresh={refresh}
        isLoading={isLoading}
        isSilentRefresh={isSilentRefresh}
        toolbarButtons={<></>}
      />
    </>
  );
};

export default ListReportDefinitions;
